import useDrivePicker from 'react-google-drive-picker';
import { useQuery } from '@tanstack/react-query';
import { idAccessToken, UserKeys } from '../api/users-api';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import '@googleworkspace/drive-picker-react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Grow from '@mui/material/Grow';
import Description from '@mui/icons-material/Description';
import Folder from '@mui/icons-material/Folder';

const PICKER_CONFIGS = {
  spreadsheet: {
    label: 'Select File',
    viewId: 'SPREADSHEETS',
    mimeType: 'application/vnd.google-apps.spreadsheet',
  },
  folder: {
    label: 'Select Folder',
    viewId: 'FOLDERS',
    mimeType: 'application/vnd.google-apps.folder',
    setSelectFolderEnabled: true,
  },
} as const;

export interface SharedFilePickerProps {
  // Required props
  pickerType: 'spreadsheet' | 'folder';
  selectedFileId: string;
  selectedFileName: string;
  onFileSelected: (file: CallbackDoc) => void;
  onFileRemoved: () => void;

  // Optional props
  maxWidth?: string;
  errorMessage?: string;
  includeSharedDrives?: boolean;
  setParentFolder?: string;
  onBeforeOpen?: () => Promise<boolean> | boolean;
  onPermissionDenied?: () => void;
  multiselect?: boolean;
  showUploadFolders?: boolean;
  showUploadView?: boolean;
  disabled?: boolean;
  customTrigger?: (openPicker: () => void) => React.ReactNode;
}

export default function SharedFilePickerOld({
  pickerType,
  selectedFileId,
  selectedFileName,
  onFileSelected,
  onFileRemoved,
  maxWidth,
  errorMessage,
  setParentFolder,
  onBeforeOpen,
  onPermissionDenied,
  includeSharedDrives = false,
  multiselect = false,
  showUploadFolders = false,
  showUploadView = false,
  disabled = false,
  customTrigger,
}: SharedFilePickerProps) {
  const [openPicker] = useDrivePicker();
  const { data: accessToken } = useQuery({
    queryKey: [UserKeys.AccessToken],
    queryFn: () => idAccessToken(),
    staleTime: Infinity,
  });

  const config = PICKER_CONFIGS[pickerType];

  async function handleOpenPicker() {
    if (disabled || !accessToken) return;

    // Check permissions before opening
    if (onBeforeOpen) {
      const allowed = await onBeforeOpen();
      if (!allowed) {
        onPermissionDenied?.();
        return;
      }
    }

    openPicker({
      clientId: '',
      developerKey: import.meta.env.VITE_DRIVE_PICKER_API_KEY,
      token: accessToken,
      viewId: config.viewId,
      viewMimeTypes: config.mimeType,
      multiselect,
      setSelectFolderEnabled:
        config.viewId === 'FOLDERS' ? config.setSelectFolderEnabled : undefined,
      supportDrives: includeSharedDrives,
      setParentFolder,
      callbackFunction: (data) => {
        // Fix z-index for picker dialog
        const pickerElements = Array.from(
          document.getElementsByClassName(
            'picker-dialog'
          ) as HTMLCollectionOf<HTMLElement>
        );
        pickerElements.forEach((element) => {
          element.style.zIndex = '2000';
        });

        // Handle file selection
        if (data.action === 'picked' && data.docs?.length > 0) {
          const doc = data.docs[0];
          onFileSelected(doc);
        }
      },
      showUploadFolders,
      showUploadView,
    });
  }

  const hasFileSelected = Boolean(selectedFileId) && !disabled;
  const StartIcon = pickerType === 'folder' ? Folder : Description;
  const url =
    selectedFileId !== ''
      ? pickerType === 'spreadsheet'
        ? `https://docs.google.com/spreadsheets/d/${selectedFileId}`
        : `https://drive.google.com/drive/folders/${selectedFileId}`
      : '';

  return (
    <Box>
      {customTrigger ? (
        customTrigger(handleOpenPicker)
      ) : (
        <Stack
          gap={2}
          flexDirection="row"
          alignItems="center"
          sx={{ mb: 1, maxWidth: maxWidth || 600 }}
        >
          <Button
            variant="contained"
            onClick={handleOpenPicker}
            startIcon={<StartIcon />}
            loading={!accessToken}
            disabled={disabled}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {config.label}
          </Button>
          {hasFileSelected && (
            <Tooltip title={selectedFileName} arrow>
              <Chip
                avatar={<StartIcon />}
                label={selectedFileName}
                onDelete={(e) => {
                  e.preventDefault();
                  onFileRemoved();
                }}
                clickable={url !== ''}
                component={url !== '' ? 'a' : 'div'}
                href={url !== '' ? url : undefined}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flex: 1,
                  maxWidth: 'fit-content',
                  minWidth: 0,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      )}
      {errorMessage && (
        <Grow in={true}>
          <Alert severity="error" sx={{ width: 'fit-content' }}>
            {errorMessage}
          </Alert>
        </Grow>
      )}
    </Box>
  );
}
